import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import './i18n';
import { AsyncProviderConfig, asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { AnalyticsProvider } from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';

import App from './App';
import ErrorBoundary from './views/ErrorBoundary';
import { GlobalProvider } from './providers';
import { environment } from './environments/environment';

const getWindow = () => window as any;

const roots = new Map<string, Root>();

getWindow().renderDocumentTemplates = async (
  containerId: string,
  currentUser: CurrentUser,
  analytics: SegmentAnalytics.AnalyticsJS,
) => {
  const ldProviderConfig: AsyncProviderConfig = {
    clientSideID: environment.launchDarklyClientKey,
    context: {
      kind: 'user',
      key: currentUser?.companyId + '',
      loggedInUserId: currentUser?.userId + '',
      createdTime: currentUser?.company?.createdTime + '',
      status: currentUser?.company?.status,
    },
    options: {},
  };
  if (process.env.NODE_ENV === 'development') {
    ldProviderConfig.flags = {
      ...ldProviderConfig.flags,
      multilevelListEnabled: true,
    };
  }
  const LDProvider = await asyncWithLDProvider(ldProviderConfig);

  getWindow().templateEditor = {};

  getWindow().templateEditor.containerHtmlElement = document.getElementById(containerId);

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container #${containerId} not found`);
    return;
  }

  const root = createRoot(container);
  roots.set(containerId, root);

  root.render(
    <ErrorBoundary>
      <LDProvider>
        <GlobalProvider currentUser={currentUser}>
          <AnalyticsProvider analytics={analytics}>
            <App />
          </AnalyticsProvider>
        </GlobalProvider>
      </LDProvider>
    </ErrorBoundary>,
  );
};

getWindow().unmountDocumentTemplates = (containerId: string) => {
  try {
    const root = roots.get(containerId);
    if (root) {
      root.unmount();
      roots.delete(containerId);
    }
  } catch (e) {
    // even with the if statements its possible we have a race condition that is the if states the element exists BUT
    // then before we remove it the Application container has removed it
  }
};
